import { ListToolbar, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { NewPolarQuotePanel } from './NewPolarQuotePanel';
import { Spinner } from '../ui/Spinner';
import { useBillingDocuments, useBillingLineNatures, useBillingPaymentsSync, useMarkBillingPaid, useVoidBillingDocument } from '../../hooks/queries/useBillingDocuments';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import {
  BILLING_DOC_TYPE_LABELS,
  formatEuros,
  type BillingDocument,
  type BillingDocumentStatus,
} from '../../services/billing/billingService';
import { polarDocumentNumber, polarStatusLabel } from '../../services/billing/polarDisplay';
import { cn } from '../../lib/cn';

import { formatAppDate } from '../../lib/dateFormat';
import { ResizableColumns } from '../ui/ResizableColumns';
import { FilterPill } from '../ui/FilterPill';
import { FaFileInvoice, FaMoneyCheckDollar, FaReceipt } from '../ui/FaIcons';
import { LINE_NATURE_LABELS } from '../../lib/billing/lineTotals';
import { BillingSummaryPanel, NATURE_DOT } from './BillingSummaryPanel';
import { BillingPaymentsTab } from './BillingPaymentsTab';
import { BillingExpensesTab } from './BillingExpensesTab';
import {
  BILLING_PERIOD_LABELS,
  compositionByDocument,
  matchesBillingPeriod,
  remainingToPay,
  summarizeInvoices,
  type BillingPeriod,
} from '../../lib/billing/invoiceList';


type BillingTab = 'factures' | 'reglements' | 'frais';

const BILLING_TABS: { key: BillingTab; label: string; icon: typeof FaFileInvoice }[] = [
  { key: 'factures', label: 'Factures', icon: FaFileInvoice },
  { key: 'reglements', label: 'Règlements', icon: FaMoneyCheckDollar },
  { key: 'frais', label: 'Frais & débours', icon: FaReceipt },
];

function isBillingTab(value: string | null): value is BillingTab {
  return value === 'factures' || value === 'reglements' || value === 'frais';
}

/** Page Facturation : trois onglets, l'onglet actif dans l'adresse (?onglet=). */
export function BillingListPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('onglet');
  const tab: BillingTab = isBillingTab(raw) ? raw : 'factures';
  const [invoiceCount, setInvoiceCount] = useState<number | null>(null);
  const [paymentCount, setPaymentCount] = useState<number | null>(null);
  const [expenseCount, setExpenseCount] = useState<number | null>(null);

  function select(next: BillingTab) {
    const nextParams = new URLSearchParams(params);
    if (next === 'factures') nextParams.delete('onglet');
    else nextParams.set('onglet', next);
    setParams(nextParams, { replace: true });
  }

  return (
    <div className="main-container-gdm space-y-3">
      <nav aria-label="Onglets de la facturation" className="flex items-center border-b border-[#e6e2d8]">
        {BILLING_TABS.map(({ key, label, icon: Icon }) => {
          const active = key === tab;
          const count = key === 'factures' ? invoiceCount : key === 'reglements' ? paymentCount : expenseCount;
          const hot = key === 'frais' && !!expenseCount;
          return (
            <button
              key={key}
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => select(key)}
              className={cn(
                '-mb-px inline-flex items-center whitespace-nowrap border-b-2 px-3 pb-2.5 pt-[11px] text-sm transition-colors',
                active
                  ? 'border-cime-primary font-semibold text-cime-primary'
                  : 'border-transparent font-medium text-cime-text-muted hover:text-cime-text-primary',
              )}
            >
              <Icon className={cn('mr-[7px] h-[13px] w-[13px]', active ? 'opacity-100' : 'opacity-70')} aria-hidden />
              {label}
              {count !== null ? (
                <span
                  className={cn(
                    'ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none',
                    hot ? 'bg-[#fdece6] text-[#c8431f]' : active ? 'bg-[#e8f0ec] text-cime-primary' : 'bg-neutral-100 text-neutral-400',
                  )}
                >
                  {hot ? `${count} à refacturer` : count}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
      {tab === 'factures' ? (
        <BillingInvoicesTab onCount={setInvoiceCount} />
      ) : tab === 'reglements' ? (
        <BillingPaymentsTab onCount={setPaymentCount} />
      ) : (
        <BillingExpensesTab onCount={setExpenseCount} />
      )}
    </div>
  );
}

const STATUS_CLASSES: Record<BillingDocumentStatus, string> = {
  draft:    'bg-neutral-100 text-[#737373]',
  sent:     'bg-[#e9effa] text-[#2b5aa8]',
  paid:     'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  overdue:  'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
  void:     'bg-neutral-100 text-neutral-400',
  accepted: 'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  rejected: 'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
};

function BillingStatusControl({ doc }: { doc: BillingDocument }) {
  const role = asCollaboratorRole(useAuthStore((s) => s.collaborator?.role));
  const markPaid = useMarkBillingPaid();
  const voidDoc = useVoidBillingDocument();
  const label = polarStatusLabel(doc);
  const tone: BillingDocumentStatus = label === 'Figé' ? 'sent' : doc.status;
  const polarLocked = !!(polarDocumentNumber(doc) && (doc.polar_quote_id || doc.polar_invoice_id));
  const canPay =
    doc.doc_type !== 'estimate' &&
    (doc.status === 'sent' || doc.status === 'overdue') &&
    canAccessFeature(role, 'billing_mark_paid');
  const canVoid =
    (doc.status === 'draft' || doc.status === 'sent' || doc.status === 'overdue') &&
    !polarLocked &&
    canAccessFeature(role, 'billing_void');
  const busy = markPaid.isPending || voidDoc.isPending;

  if (!canPay && !canVoid) {
    return (
      <span className={cn('inline-flex h-[22px] items-center rounded-full border border-transparent px-2.5 text-xs font-semibold', STATUS_CLASSES[tone])}>
        {label}
      </span>
    );
  }

  return (
    <select
      aria-label={`Statut de ${polarDocumentNumber(doc) ?? 'ce document'}`}
      disabled={busy}
      value=""
      onChange={(e) => {
        const next = e.target.value;
        e.currentTarget.value = '';
        if (!next) return;
        void (async () => {
          try {
            if (next === 'paid') {
              await markPaid.mutateAsync({
                id: doc.id,
                paid_at: new Date().toISOString().slice(0, 10),
                payment_method: 'Virement',
              });
              toast.success('Marquée payée (virement, aujourd’hui)');
              return;
            }
            if (next === 'void') {
              if (!window.confirm('Annuler ce document ? Les prestations liées seront libérées.')) return;
              await voidDoc.mutateAsync(doc.id);
              toast.success('Document annulé');
            }
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Changement de statut impossible');
          }
        })();
      }}
      className={cn(
        'h-[24px] max-w-[9.5rem] cursor-pointer rounded-full border border-transparent px-2 text-xs font-semibold',
        STATUS_CLASSES[tone],
      )}
    >
      <option value="">{label}</option>
      {canPay && <option value="paid">Payée</option>}
      {canVoid && <option value="void">Annulée</option>}
    </select>
  );
}

const NO_FILTERS = {};
const DEFAULT_PERIOD: BillingPeriod = 'year';
const PERIOD_OPTIONS = (['month', 'last_month', 'year'] as const).map((value) => ({
  value,
  label: BILLING_PERIOD_LABELS[value],
}));
const TYPE_OPTIONS = [
  { value: 'invoice', label: 'Facture' },
  { value: 'estimate', label: 'Devis' },
  { value: 'credit_note', label: 'Avoir' },
];
const STATUS_OPTIONS: { value: BillingDocumentStatus; label: string }[] = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'sent', label: 'Envoyée' },
  { value: 'overdue', label: 'En retard' },
  { value: 'paid', label: 'Payée' },
  { value: 'accepted', label: 'Devis accepté' },
  { value: 'rejected', label: 'Devis refusé' },
  { value: 'void', label: 'Annulée' },
];

function BillingInvoicesTab({ onCount }: { onCount: (count: number) => void }) {
  const navigate = useNavigate();
  const canCreate = canAccessFeature(
    asCollaboratorRole(useAuthStore((s) => s.collaborator?.role)),
    'billing_create',
  );
  const [creating, setCreating] = useState<'invoice' | 'estimate' | null>(null);
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<BillingPeriod>(DEFAULT_PERIOD);
  const filtersChanged = docTypes.length > 0 || statuses.length > 0 || period !== DEFAULT_PERIOD;

  function resetFilters() {
    setDocTypes([]);
    setStatuses([]);
    setPeriod(DEFAULT_PERIOD);
  }

  // Type et statut se filtrent ici : plusieurs choix possibles dans chaque pastille.
  const { data: docs, isLoading } = useBillingDocuments(NO_FILTERS);
  // Règlements saisis dans Polaris : recopiés à l'ouverture de la page.
  useBillingPaymentsSync(docs);
  const { data: dossiers } = useDossiers({ include_archived: true, include_closed: true });

  const dossierMap = useMemo(() => {
    const m = new Map<string, { number: string; title: string }>();
    for (const d of dossiers ?? []) m.set(d.id, { number: d.number, title: d.title });
    return m;
  }, [dossiers]);

  const filtered = useMemo(() => {
    if (!docs) return [];
    const now = new Date();
    const inPeriod = docs.filter(
      (d) =>
        matchesBillingPeriod(d, period, now) &&
        (docTypes.length === 0 || docTypes.includes(d.doc_type)) &&
        (statuses.length === 0 || statuses.includes(d.status)),
    );
    if (!search.trim()) return inPeriod;
    const s = search.trim().toLowerCase();
    return inPeriod.filter((d) => {
      const num = (d.number ?? '').toLowerCase();
      const dossier = dossierMap.get(d.dossier_id);
      const title = dossier?.title.toLowerCase() ?? '';
      const dnum = dossier?.number.toLowerCase() ?? '';
      return num.includes(s) || title.includes(s) || dnum.includes(s);
    });
  }, [docs, search, dossierMap, period, docTypes, statuses]);

  useEffect(() => {
    if (!isLoading) onCount(filtered.length);
  }, [filtered.length, isLoading, onCount]);

  const docIds = useMemo(() => filtered.map((d) => d.id), [filtered]);
  const { data: natureRows } = useBillingLineNatures(docIds);
  const composition = useMemo(() => compositionByDocument(natureRows ?? []), [natureRows]);
  const summary = useMemo(
    () => summarizeInvoices(filtered, composition, new Map(filtered.map((d) => [d.id, Number(d.paid_amount ?? 0)]))),
    [filtered, composition],
  );


  if (creating) {
    return (
      <NewPolarQuotePanel docType={creating} onCancel={() => setCreating(null)} />
    );
  }

  return (
    <div className="space-y-3">
      {/* Barre d'outils : toujours au-dessus des chiffres */}
      <ListToolbar>
        <ToolbarSearch
          value={search}
          onChange={setSearch}
          placeholder="Rechercher par numéro, dossier, titre…"
          ariaLabel="Rechercher une pièce"
          className="w-[300px]"
        />
        <FilterPill
          mode="single"
          label="Période"
          options={PERIOD_OPTIONS}
          selected={period === 'all' ? [] : [period]}
          onChange={(values) => setPeriod((values[0] as BillingPeriod | undefined) ?? 'all')}
        />
        <FilterPill label="Type" options={TYPE_OPTIONS} selected={docTypes} onChange={setDocTypes} />
        <FilterPill label="Statut" options={STATUS_OPTIONS} selected={statuses} onChange={setStatuses} />
        {filtersChanged && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-[#8a928c] underline underline-offset-2 hover:text-[#1f2823]"
          >
            Réinitialiser
          </button>
        )}
        <ToolbarGrow />
        {canCreate && (
          <>
            <Button variant="secondary" onClick={() => setCreating('estimate')}>
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau devis
            </Button>
            <Button variant="primary" onClick={() => setCreating('invoice')}>
              <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture
            </Button>
          </>
        )}
      </ListToolbar>

      <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_272px]">
      <div className="min-w-0">
      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-neutral-500">
            <ReceiptText className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
            <p className="text-sm">{docs && docs.length > 0 ? 'Aucune pièce ne correspond à ces filtres.' : 'Aucun document de facturation.'}</p>
            <p className="text-xs text-neutral-400 mt-2">
              {canCreate
                ? 'Nouvelle facture ou Nouveau devis, en haut de la liste.'
                : 'Aucun document.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="cime-table text-[13px]">
            <ResizableColumns id="billing-v2" />
            <thead>
              <tr>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Pièce</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Dossier</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Type</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-right">Total TTC</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-right">Reste à régler</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Statut</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Échéance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const dossier = dossierMap.get(d.dossier_id);
                const parts = composition.get(d.id) ?? [];
                const rest = remainingToPay(d, Number(d.paid_amount ?? 0));
                return (
                  <tr
                    key={d.id}
                    onClick={() => navigate(`/facturation/${d.id}`)}
                    className="cursor-pointer"
                  >
                    <td className="max-w-[280px] px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                      <span className="block text-[11px] tracking-wide text-neutral-400">
                        {polarDocumentNumber(d) ?? <span className="italic">Brouillon</span>}
                      </span>
                      <span className="block truncate text-[13px] font-medium text-cime-text-primary">
                        {d.notes?.trim() || `${BILLING_DOC_TYPE_LABELS[d.doc_type]}${dossier ? ` — ${dossier.title}` : ''}`}
                      </span>
                      {parts.length > 0 ? (
                        <span className="mt-[3px] flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-cime-text-muted">
                          {parts.map((part) => (
                            <span key={part.nature} className="inline-flex items-center gap-[5px] whitespace-nowrap tabular-nums">
                              <i aria-hidden className={cn('h-[7px] w-[7px] flex-none rounded-[2px]', NATURE_DOT[part.nature])} />
                              {LINE_NATURE_LABELS[part.nature]} {formatEuros(part.ht)}
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-[200px] px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                      {dossier ? (
                        <span title={dossier.title}>
                          <span className="block text-[11px] tracking-wide text-neutral-400">{dossier.number}</span>
                          <span className="block truncate text-[13px] text-cime-text-primary">{dossier.title}</span>
                        </span>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px]">
                      <span className="inline-flex h-5 items-center rounded-full border border-neutral-300 px-2 text-[11px] font-semibold text-cime-text-muted">
                        {BILLING_DOC_TYPE_LABELS[d.doc_type]}
                      </span>
                    </td>
                    <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right tabular-nums text-cime-text-primary">
                      {formatEuros(d.ttc_total)}
                    </td>
                    <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right tabular-nums">
                      {rest ? (
                        <span className="font-semibold text-cime-text-primary">{formatEuros(rest)}</span>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px]" onClick={(e) => e.stopPropagation()}>
                      <BillingStatusControl doc={d} />
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                      <span className={`block text-[13px] ${d.status === 'overdue' ? 'font-semibold text-cime-urgent' : 'text-cime-text-primary'}`}>
                        {d.due_date ? formatAppDate(d.due_date) : '—'}
                      </span>
                      {d.issue_date ? (
                        <span className="block text-[11px] text-neutral-400">Émission {formatAppDate(d.issue_date)}</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
      <BillingSummaryPanel summary={summary} />
      </div>
    </div>
  );
}
